import { absencePolicySchema, sessionBalanceSchema } from './clinicSessions.js';
import { z } from 'zod';
import { businessHoursSchema } from './business.js';
import { saudeDocumentSchema } from './clinicDocuments.js';
import { assessmentTypeSchema, clinicAppointmentSchema, clinicKindSchema, programProgressSchema, treatmentEventSchema, } from './clinic.js';
/**
 * Saúde: o lado do paciente (estudo E9, aprovado em 21/10/2026). A pessoa vê o que
 * cada clínica registra sobre ela depois de aceitar a ligação pelo telefone, confirma e
 * remarca a consulta, e acompanha sozinha o que quiser em "Meus registros", cifrado no
 * aparelho: o servidor guarda o pacote e não lê.
 */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const isoDateTime = z.iso.datetime();
export const saudeTreatmentSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    phase: z.number().int().min(0),
    phases: z.array(z.string()),
    startDay: isoDate,
    months: z.number().int().min(1).nullable(),
    monthOf: z.number().int().min(1).nullable(),
    nextReviewDay: isoDate.nullable(),
    status: z.enum(['ativo', 'finalizado', 'cancelado']),
});
export const saudeAppointmentSchema = clinicAppointmentSchema.pick({
    id: true,
    label: true,
    startAt: true,
    durationMin: true,
    status: true,
    mode: true,
    videoUrl: true,
});
/** O que a pessoa precisa fazer naquela clínica agora. */
export const SAUDE_PENDING = ['confirmar', 'pagar'];
export const saudeClinicCardSchema = z.object({
    clinicId: z.uuid(),
    patientId: z.uuid(),
    name: z.string(),
    kind: clinicKindSchema,
    /** "Consultório médico · Emagrecimento…" (servidor novo). */
    title: z.string().optional(),
    registration: z.string().nullable(),
    /** Como o profissional aparece ("Dra. Paula"), quando a clínica definiu. */
    professional: z.string().nullable(),
    treatment: saudeTreatmentSchema.nullable(),
    nextAppointment: saudeAppointmentSchema.nullable(),
    pending: z.enum(SAUDE_PENDING).nullable(),
    /** O que mudou desde a última abertura, em uma frase; null quando nada. */
    changes: z.string().nullable(),
    linkedAt: isoDateTime.nullable(),
    /** O que a pessoa compartilha com esta clínica (para o app mandar o valor ao registrar). */
    sharing: z.object({ peso: z.boolean(), medidas: z.boolean() }),
    /** Versão da marca redonda da clínica (T7.25); sem ela, as iniciais. */
    logoVersion: z.number().int().nullable().optional(),
    /** Documentos emitidos nos últimos 14 dias (o Painel da Início avisa). */
    newDocuments: z.number().int().min(0).optional(),
    /** O documento mais novo, para o Painel dizer o que chegou. */
    latestDocument: z.object({ title: z.string(), issuedAt: isoDateTime }).nullable().optional(),
});
export const saudeInviteSchema = z.object({
    patientId: z.uuid(),
    clinicId: z.uuid(),
    name: z.string(),
    kind: clinicKindSchema,
    /** "Consultório médico · Emagrecimento…" (servidor novo). */
    title: z.string().optional(),
});
/**
 * O corpo hoje pela clínica: a bioimpedância mais nova entre as clínicas ligadas (só a que
 * a clínica deixou o paciente ver). A Saúde mostra no alto e o aparelho guarda no histórico.
 */
export const saudeBodySchema = z.object({
    assessmentId: z.uuid(),
    clinicId: z.uuid(),
    clinicName: z.string(),
    measuredAt: isoDateTime,
    weightKg: z.number().optional(),
    fatPercent: z.number().optional(),
    muscleKg: z.number().optional(),
    waistCm: z.number().optional(),
    hipCm: z.number().optional(),
    waterPercent: z.number().optional(),
    visceralLevel: z.number().optional(),
});
export const saudeOverviewSchema = z.object({
    clinics: z.array(saudeClinicCardSchema),
    invites: z.array(saudeInviteSchema),
    body: saudeBodySchema.nullable().optional(),
    /** Registros próprios: só a contagem e a data; o conteúdo é cifrado no aparelho. */
    records: z.object({ count: z.number().int().min(0), lastAt: isoDateTime.nullable() }),
});
export const saudeAssessmentSchema = z.object({
    id: z.uuid(),
    type: assessmentTypeSchema,
    measuredAt: isoDateTime,
    data: z.record(z.string(), z.unknown()),
});
export const saudeTransactionSchema = z.object({
    id: z.uuid(),
    label: z.string(),
    amountCents: z.number().int(),
    dueDay: isoDate,
    paidAt: isoDateTime.nullable(),
    status: z.enum(['pago', 'previsto', 'atrasado']),
});
/** O que a pessoa compartilha com esta clínica, por tipo de registro próprio. */
export const saudeSharingSchema = z.object({
    peso: z.boolean(),
    medidas: z.boolean(),
    /** P3: o personal vê o resumo do diário alimentar (adesão ao plano). */
    equipe_dieta: z.boolean().optional(),
    /** P3: a nutricionista vê a carga e a frequência do treino. */
    equipe_treino: z.boolean().optional(),
});
export const saudeClinicDetailSchema = z.object({
    clinic: z.object({
        id: z.uuid(),
        name: z.string(),
        kind: clinicKindSchema,
        /** "Consultório médico · Emagrecimento…" (servidor novo). */
        title: z.string().optional(),
        registration: z.string().nullable(),
        professional: z.string().nullable(),
        hours: z.array(businessHoursSchema),
        /** Versão da marca redonda da clínica (T7.25); sem ela, as iniciais. */
        logoVersion: z.number().int().nullable().optional(),
        /** Já aceitou o termo de teleconsulta desta clínica (rodada 15). */
        teleConsent: z.boolean().optional(),
    }),
    patientId: z.uuid(),
    /** Como a clínica cadastrou; escolhe o corpo do avatar (sem isso, o feminino). */
    sex: z.enum(['f', 'm']).nullable(),
    treatment: saudeTreatmentSchema.nullable(),
    /** As próximas e as últimas consultas, da mais próxima para a mais antiga. */
    appointments: z.array(saudeAppointmentSchema),
    /** Só as avaliações que a clínica marcou como visíveis ao paciente. */
    assessments: z.array(saudeAssessmentSchema),
    transactions: z.array(saudeTransactionSchema),
    events: z.array(treatmentEventSchema.pick({ id: true, kind: true, text: true, at: true })),
    sharing: saudeSharingSchema,
    linkedAt: isoDateTime.nullable(),
    /** Documentos que a clínica emitiu (área do médico, M2). */
    documents: z.array(saudeDocumentSchema).optional(),
    /** Saldo do pacote ou da mensalidade e a regra de faltas, às claras para o aluno. */
    balance: sessionBalanceSchema.nullable().optional(),
    /** Programa conjunto (P3): nutri, treino e bioimpedância, o que já foi e o que falta. */
    program: programProgressSchema.nullable().optional(),
    /** Estúdio: a regra de faltas (o aluno desmarca pelo app sabendo se desconta). */
    absence: absencePolicySchema.optional(),
    /** P3: a clínica tem nutricionista e personal na equipe (mostra o "quem vê o quê"). */
    team: z.object({ dieta: z.boolean(), treino: z.boolean() }).optional(),
});
/** Horário livre para remarcar, no dia e hora locais da clínica. */
export const saudeSlotSchema = z.object({ day: isoDate, time: z.string().regex(/^\d{2}:\d{2}$/) });
export const saudeRescheduleSchema = z.object({
    day: isoDate,
    time: z.string().regex(/^\d{2}:\d{2}$/),
});
// ── Meus registros (cifrados no aparelho) ───────────────────────────────────
export const SELF_RECORD_TYPES = [
    'peso',
    'medidas',
    'fotos',
    'habitos',
    'notas',
    'ficha',
    'vacina',
    'exame',
];
export const selfRecordTypeSchema = z.enum(SELF_RECORD_TYPES);
export const SELF_RECORD_LABEL = {
    peso: 'Peso',
    medidas: 'Medidas',
    fotos: 'Fotos',
    habitos: 'Hábitos',
    notas: 'Notas',
    ficha: 'Sua ficha',
    vacina: 'Vacinas',
    exame: 'Exames',
};
export const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
/**
 * Os marcadores mais pedidos nos exames de rotina, com o que medem em palavras simples. A
 * referência é a do laboratório da pessoa (cada um usa a sua): o app não diz se está bom.
 */
export const BIOMARKERS = [
    { key: 'glicose', name: 'Glicose em jejum', unit: 'mg/dL', what: 'O açúcar no sangue em jejum.' },
    {
        key: 'hba1c',
        name: 'Hemoglobina glicada',
        unit: '%',
        what: 'A média do açúcar nos últimos 3 meses.',
    },
    {
        key: 'colesterol',
        name: 'Colesterol total',
        unit: 'mg/dL',
        what: 'A soma das gorduras de colesterol.',
    },
    { key: 'hdl', name: 'HDL', unit: 'mg/dL', what: 'O colesterol que protege.' },
    { key: 'ldl', name: 'LDL', unit: 'mg/dL', what: 'O colesterol que se acumula nos vasos.' },
    {
        key: 'triglicerides',
        name: 'Triglicerídeos',
        unit: 'mg/dL',
        what: 'Gordura que vem da comida e do açúcar.',
    },
    { key: 'tsh', name: 'TSH', unit: 'µUI/mL', what: 'Como a tireoide está trabalhando.' },
    { key: 't4l', name: 'T4 livre', unit: 'ng/dL', what: 'O hormônio da tireoide em circulação.' },
    { key: 'vitd', name: 'Vitamina D', unit: 'ng/mL', what: 'A reserva de vitamina D do corpo.' },
    { key: 'b12', name: 'Vitamina B12', unit: 'pg/mL', what: 'Vitamina dos nervos e do sangue.' },
    { key: 'ferritina', name: 'Ferritina', unit: 'ng/mL', what: 'O estoque de ferro.' },
    { key: 'hemoglobina', name: 'Hemoglobina', unit: 'g/dL', what: 'O que leva oxigênio no sangue.' },
    { key: 'creatinina', name: 'Creatinina', unit: 'mg/dL', what: 'Como os rins estão filtrando.' },
    { key: 'tgp', name: 'TGP (ALT)', unit: 'U/L', what: 'Uma enzima do fígado.' },
    {
        key: 'acido_urico',
        name: 'Ácido úrico',
        unit: 'mg/dL',
        what: 'O que pode virar cristal nas juntas.',
    },
    { key: 'pcr', name: 'PCR', unit: 'mg/L', what: 'Sinal de inflamação no corpo.' },
    {
        key: 'testosterona',
        name: 'Testosterona total',
        unit: 'ng/dL',
        what: 'O hormônio masculino principal.',
    },
    { key: 'psa', name: 'PSA', unit: 'ng/mL', what: 'Acompanhamento da próstata.' },
    {
        key: 'insulina',
        name: 'Insulina em jejum',
        unit: 'µUI/mL',
        what: 'O hormônio que guarda o açúcar.',
    },
];
/** Pacote cifrado no aparelho (AES-GCM, base64), até 400 KB (uma foto reduzida cabe). */
export const SELF_RECORD_MAX_BYTES = 400 * 1024;
export const selfRecordSchema = z.object({
    id: z.uuid(),
    type: selfRecordTypeSchema,
    at: isoDateTime,
    ciphertext: z.string().min(1).max(SELF_RECORD_MAX_BYTES),
    createdAt: isoDateTime,
});
export const createSelfRecordSchema = z.object({
    type: selfRecordTypeSchema,
    at: isoDateTime.optional(),
    ciphertext: z.string().min(1).max(SELF_RECORD_MAX_BYTES),
});
/** O que o aparelho guarda dentro do pacote, por tipo (nunca sai em claro, salvo ao compartilhar). */
export const selfRecordPayloadSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('peso'), kg: z.number().min(10).max(400) }),
    z.object({
        type: z.literal('medidas'),
        cinturaCm: z.number().min(30).max(250).optional(),
        quadrilCm: z.number().min(30).max(250).optional(),
        bracoCm: z.number().min(10).max(100).optional(),
        coxaCm: z.number().min(20).max(150).optional(),
    }),
    z.object({
        type: z.literal('fotos'),
        dataUrl: z.string().min(1),
        note: z.string().max(120).optional(),
    }),
    z.object({
        type: z.literal('habitos'),
        done: z.array(z.string().trim().min(1).max(40)).max(20),
    }),
    z.object({ type: z.literal('notas'), text: z.string().trim().min(1).max(2000) }),
    z.object({
        type: z.literal('ficha'),
        bloodType: z.enum(BLOOD_TYPES).optional(),
        allergies: z.string().trim().max(300).optional(),
        conditions: z.string().trim().max(300).optional(),
        medications: z.string().trim().max(400).optional(),
    }),
    z.object({
        type: z.literal('vacina'),
        name: z.string().trim().min(1).max(80),
        dose: z.string().trim().max(30).optional(),
        day: isoDate.optional(),
        place: z.string().trim().max(80).optional(),
    }),
    z.object({
        type: z.literal('exame'),
        day: isoDate,
        lab: z.string().trim().max(80).optional(),
        markers: z
            .array(z.object({
            key: z.string().max(30).optional(),
            name: z.string().trim().min(1).max(60),
            value: z.number().min(0).max(100000),
            unit: z.string().trim().max(16),
            /** A faixa de referência que vem no laudo, como está escrita ("70 a 99"). */
            reference: z.string().trim().max(40).optional(),
        }))
            .min(1)
            .max(40),
    }),
]);
/** Compartilhar um tipo com uma clínica: liga ou desliga; ligado, o app manda os valores. */
export const saudeShareSchema = z.object({
    type: z.enum(['peso', 'medidas', 'equipe_dieta', 'equipe_treino']),
    enabled: z.boolean(),
});
/** Valor em claro que o app manda à clínica quando o tipo está compartilhado. */
/** O que o Galileu devolve para os registros próprios (só números; o aparelho guarda cifrado). */
export const saudeGalileuSchema = z.object({
    measuredAt: isoDateTime,
    weightKg: z.number().optional(),
    waistCm: z.number().optional(),
    fatPercent: z.number().optional(),
    muscleKg: z.number().optional(),
});
export const saudeSharedValueSchema = z.object({
    type: z.enum(['peso', 'medidas']),
    measuredAt: isoDateTime.optional(),
    data: z.record(z.string(), z.number()),
});
